<script setup lang="ts">
import { ImgHTMLAttributes, toRef } from 'vue'
import type { Product, PropertyGroupOption } from '~/types/shopware'
import { getProductTranslatedCustomFields } from '~/types/shopware'
import { useSeoUrls } from '~/composables/useSeoUrls'
import { useI18n } from 'vue-i18n'

const { getFormattedPrice } = usePrice()
const router = useRouter()

const { t } = useI18n()

const props = defineProps<{
  product: Product
}>()

const description = computed(() => {
  const customFields = getProductTranslatedCustomFields(props.product)
  return customFields?.custom_product_fields_subtitle || ''
})

const types = computed(() => {
  const list = props.product.properties as unknown as Array<
    PropertyGroupOption & {
      translated?: {
        customFields?: {
          custom_property_fields_type_property_group_code?: string
          custom_property_fields_type_property_code?: string
        }
      }
    }
  >
  return (
    list
      ?.filter(
        (item) =>
          item.translated?.customFields?.custom_property_fields_type_property_group_code === 'product-type-group',
      )
      .map((item) => ({
        name: item.name,
        code: item.translated?.customFields?.custom_property_fields_type_property_code,
      })) || []
  )
})

const { useProductSeoPath } = useSeoUrls()
const path = useProductSeoPath(toRef(props, 'product'))
const image = computed(() => {
  return {
    src: props.product?.cover?.media?.url || '',
    width: props.product?.cover?.media?.metaData?.width || 140,
    height: props.product?.cover?.media?.metaData?.height || 200,
    alt: props.product?.cover?.media?.alt || props.product.translated?.name || 'product image',
    title: props.product?.cover?.media?.title || props.product.translated?.name || 'product image',
    loading: 'lazy',
  } as ImgHTMLAttributes
})

const onClick = () => router.push(path.value)
</script>
<template>
  <article class="product" @click.stop="onClick">
    <ShopProductLabels :product="product" />
    <img v-if="image.src" class="image" v-bind="image" />
    <img
      v-else
      src="data:image/svg+xml;base64,PHN2ZyB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IiB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWw6c3BhY2U9InByZXNlcnZlIj4KIDxyZWN0IGZpbGw9InVybCgjbXlHcmFkaWVudCkiICB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIi8+CiAgPGcgaWQ9IkxvZ28iID4KIDxnIGlkPSJYTUxJRF85ODVfIj4KICAgPHBhdGggaWQ9IlhNTElEXzk4N18iIHRyYW5zZm9ybT0idHJhbnNsYXRlKC04MCwgLTQwKSIgZmlsbD0iI0ZGRkZGRiIgZD0iTTE4OC40NSwxNzEuMzFjLTYuODMtNS4xNC0xNi44OS05LjA0LTI3LjU0LTEzLjE3Yy0xMi42OC00LjkyLTI3LjA1LTEwLjQ5LTM3Ljg0LTE5LjA3CiAgICAgYy0xMi4yMi05LjczLTE4LjE3LTIyLTE4LjE3LTM3LjUzYzAtMTMuOTMsNS43OC0yNS44NCwxNi43Mi0zNC40NWMxMi4yNi05LjY1LDMwLjg0LTE0Ljc2LDUzLjcyLTE0Ljc2CiAgICAgYzYuMzIsMCwxMi4zNiwwLjM5LDE3Ljk2LDEuMTZjMC40OSwwLjA3LDAuOTctMC4yLDEuMTctMC42NGMwLjIxLTAuNDUsMC4xLTAuOTgtMC4yNy0xLjMxYy0xNC40Mi0xMi45Ny0zMy4wNi0yMC4xMS01Mi40Ny0yMC4xMQogICAgIGMtMjAuOTgsMC00MC43MSw4LjE3LTU1LjU1LDIzLjAxYy0xNC44NCwxNC44My0yMy4wMSwzNC41Ni0yMy4wMSw1NS41NGMwLDIwLjk4LDguMTcsNDAuNzEsMjMuMDEsNTUuNTQKICAgICBjMTQuODMsMTQuODMsMzQuNTYsMjMsNTUuNTUsMjNjMTYuOTUsMCwzMy4xLTUuMzIsNDYuNzEtMTUuMzljMC4yOC0wLjIxLDAuNDYtMC41NSwwLjQ2LTAuOQogICAgIEMxODguOTEsMTcxLjg2LDE4OC43NCwxNzEuNTIsMTg4LjQ1LDE3MS4zMXoiLz4KICAgPHBhdGggaWQ9IlhNTElEXzk4Nl8iICB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtODAsIC00MCkiICBmaWxsPSIjRkZGRkZGIiBkPSJNMjE5Ljk1LDEwMi43Yy0wLjAzLTAuMy0wLjE3LTAuNTgtMC40LTAuNzdjLTE2LjM5LTEzLjYtMjkuNjUtMTkuMTUtNDUuNzYtMTkuMTUKICAgICBjLTguNiwwLTE1LjE5LDEuNzItMTkuNTksNS4xMmMtMy44MSwyLjk0LTUuODMsNy4wNS01LjgzLDExLjg2YzAsMTMuNDksMTYuNDksMTkuNjQsMzUuNTgsMjYuNzYKICAgICBjOS44NCwzLjY3LDIwLjAxLDcuNDYsMjkuMzUsMTIuNjVjMC4xNywwLjA5LDAuMzYsMC4xNCwwLjU1LDAuMTRjMC4xMywwLDAuMjYtMC4wMiwwLjM4LTAuMDdjMC4zMS0wLjExLDAuNTUtMC4zNSwwLjY3LTAuNjUKICAgICBjMy41Ny05LjE0LDUuMzgtMTguNzcsNS4zOC0yOC42M0MyMjAuMjgsMTA3LjY1LDIyMC4xNywxMDUuMiwyMTkuOTUsMTAyLjd6Ii8+CiA8L2c+CiAKPC9nPgogPGxpbmVhckdyYWRpZW50IGlkPSJteUdyYWRpZW50IiBncmFkaWVudFRyYW5zZm9ybT0icm90YXRlKDIwKSI+CiAgIDxzdG9wIG9mZnNldD0iMCUiICBzdG9wLWNvbG9yPSIjZWVlIj4KICAgICA8YW5pbWF0ZSBhdHRyaWJ1dGVOYW1lPSJzdG9wLWNvbG9yIiB2YWx1ZXM9IiNFRUVFRUU7ICNDQ0NDQ0M7ICNFRUVFRUUiIGR1cj0iMnMiIHJlcGVhdENvdW50PSJpbmRlZmluaXRlIj48L2FuaW1hdGU+CiAgIDwvc3RvcD4KICAgPHN0b3Agb2Zmc2V0PSIxMDAlIiBzdG9wLWNvbG9yPSIjYWFhIj4KICAgICA8YW5pbWF0ZSBhdHRyaWJ1dGVOYW1lPSJzdG9wLWNvbG9yIiB2YWx1ZXM9IiNFRUVFRUU7ICNEREREREQ7ICNFRUVFRUUiIGR1cj0iM3MiIHJlcGVhdENvdW50PSJpbmRlZmluaXRlIj48L2FuaW1hdGU+CiAgIDwvc3RvcD4KIDwvbGluZWFyR3JhZGllbnQ+Cjwvc3ZnPg=="
      alt="Image Placeholder"
      width="140"
      height="200"
      class="placeholder"
      :title="product.translated?.name || 'Image Placeholder'"
    />

    <h3 class="text-small">
      <NuxtLink :to="path">
        {{ product.translated.name }}
      </NuxtLink>
    </h3>
    <p v-if="description" class="text-xsmall">
      {{ description }}
    </p>

    <ul v-if="types" class="cul types">
      <li v-for="(item, index) in types" :key="index" class="f-13">
        <Icon v-if="item.code === 'e-book'" class="variant-icon" name="ebook" />
        <Icon v-if="item.code === 'print'" class="variant-icon" name="book" />
        <Icon v-if="item.code === 'learning-card'" class="variant-icon" name="showcases" />
        <Icon v-if="item.code === 'combi'" class="variant-icon" name="combo" />
        <Icon v-if="item.code === 'set'" class="variant-icon" name="set" />
        {{ item.name }}
      </li>
    </ul>
    <div class="price-wrapper">
      <div class="price text-small">
        {{ getFormattedPrice(product.calculatedPrice.totalPrice) }}
        <span v-if="product.calculatedPrice.listPrice" class="text-xsmall">
          {{ t('shop.listingPage.price.insteadOf') }}
          {{ getFormattedPrice(product.calculatedPrice.listPrice.price) }}
        </span>
      </div>
      <ShopAddToCartButton class="add-to-cart" :product="product" :label="t('shop.listingPage.buyButton.label')" />
    </div>
  </article>
</template>
<style scoped lang="scss">
.product {
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 24px 48px;
  background-color: var(--c-background-medium);
  height: 100%;
  cursor: pointer;

  .labels {
    position: absolute;
    top: 24px;
    left: 24px;
  }
  img {
    display: block;
    max-height: 256px;
    width: auto;
    margin: 0 auto 24px auto;
  }
  h3 {
    margin-bottom: 0;
  }
  p {
    color: var(--c-gray-100);
    margin-bottom: 0;
  }
  .types {
    margin-top: 32px;
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    text-transform: uppercase;
    font-weight: bold;
    color: var(--c-primary);
    li {
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }
  .price-wrapper {
    padding-top: 32px;
    @include breakpoint(tl) {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: auto;
    }
    .price {
      display: grid;
      font-weight: bold;
      color: var(--c-primary);
      margin-bottom: 16px;
      @include breakpoint(tl) {
        margin-bottom: 0;
      }
      span {
        font-weight: normal;
        text-decoration: line-through;
        color: var(--c-gray-75);
      }
    }
  }
}
</style>
